'use client';
import { useCallback, useMemo, useRef, useState } from 'react';

import {
  LayoutSidebarLeft,
  LayoutSidebarLeftDock,
  LayoutSidebarLeftOff,
  LayoutSidebarRight,
  LayoutSidebarRightDock,
  LayoutSidebarRightOff,
} from '@lichtblick/icons';

import { DrawerPanel } from '../../Dialog/DrawerPanel';
import { useScrollShadows } from '../../ScrollShadows';
import { SidebarControlButton } from '../Controls';
import { PANEL_LAYOUT_DEFAULTS } from '../PanelLayout.constants';
import {
  FloatingSidebarControls,
  InnerPanelRoot,
  LeftPanelSidebar,
  LeftPanelSidebarInner,
  LeftPanelSidebarScrollArea,
  LeftPanelSidebarScrollViewport,
  MainPanel,
  MainScrollArea,
  MainScrollViewport,
  PanelContentInner,
  RightPanelSidebar,
  RightPanelSidebarInner,
  RightPanelSidebarScrollArea,
  RightPanelSidebarScrollViewport,
  ThreePanelBody,
} from '../PanelLayout.styles';

import type { CSSProperties, ReactNode } from 'react';

type ThreeColumnPanelProps = {
  children: ReactNode;
  contentPadding?: string;
  defaultLeftCollapsed?: boolean;
  defaultRightCollapsed?: boolean;
  isDrawerMode?: boolean;
  leftCollapsed?: boolean;
  leftSidebar?: ReactNode;
  leftSidebarPadded?: boolean;
  leftSidebarWidth?: string;
  mainContentPadded?: boolean;
  onLeftCollapsedChange?: (collapsed: boolean) => void;
  onRightCollapsedChange?: (collapsed: boolean) => void;
  rightCollapsed?: boolean;
  rightSidebar?: ReactNode;
  rightSidebarPadded?: boolean;
  rightSidebarWidth?: string;
  showSidebarControls?: boolean;
};

export const ThreeColumnPanel = ({
  children,
  contentPadding = PANEL_LAYOUT_DEFAULTS.contentPadding,
  defaultLeftCollapsed = false,
  defaultRightCollapsed = false,
  isDrawerMode = false,
  leftCollapsed,
  leftSidebar,
  leftSidebarPadded = false,
  leftSidebarWidth = PANEL_LAYOUT_DEFAULTS.leftSidebarWidth,
  mainContentPadded = false,
  onLeftCollapsedChange,
  onRightCollapsedChange,
  rightCollapsed,
  rightSidebar,
  rightSidebarPadded = false,
  rightSidebarWidth = PANEL_LAYOUT_DEFAULTS.rightSidebarWidth,
  showSidebarControls = true,
}: ThreeColumnPanelProps) => {
  // In drawer mode, sidebars should start collapsed
  const [leftCollapsedState, setLeftCollapsedState] = useState(defaultLeftCollapsed || isDrawerMode);
  const [rightCollapsedState, setRightCollapsedState] = useState(defaultRightCollapsed || isDrawerMode);
  const isLeftCollapsed = leftCollapsed ?? leftCollapsedState;
  const isRightCollapsed = rightCollapsed ?? rightCollapsedState;

  const styleOverrides: CSSProperties = useMemo(() => {
    const overrides: Record<string, string> = {};

    if (contentPadding !== PANEL_LAYOUT_DEFAULTS.contentPadding) {
      overrides['--layout-panel-content-padding'] = contentPadding;
    }

    if (leftSidebarWidth !== PANEL_LAYOUT_DEFAULTS.leftSidebarWidth) {
      overrides['--layout-left-sidebar-width'] = leftSidebarWidth;
    }

    if (rightSidebarWidth !== PANEL_LAYOUT_DEFAULTS.rightSidebarWidth) {
      overrides['--layout-right-sidebar-width'] = rightSidebarWidth;
    }

    return overrides as CSSProperties;
  }, [contentPadding, leftSidebarWidth, rightSidebarWidth]);

  const hasLeftSidebar = leftSidebar != null;
  const hasRightSidebar = rightSidebar != null;
  const mainScrollRef = useRef<HTMLElement>(null);
  const leftSidebarScrollRef = useRef<HTMLDivElement>(null);
  const rightSidebarScrollRef = useRef<HTMLDivElement>(null);
  const mainScrollViewportRef = useRef<HTMLDivElement>(null);
  const leftSidebarScrollViewportRef = useRef<HTMLDivElement>(null);
  const rightSidebarScrollViewportRef = useRef<HTMLDivElement>(null);

  const updateLeftCollapsed = useCallback(
    (collapsed: boolean) => {
      if (leftCollapsed != null) {
        onLeftCollapsedChange?.(collapsed);

        return;
      }

      setLeftCollapsedState(collapsed);
    },
    [leftCollapsed, onLeftCollapsedChange],
  );

  const updateRightCollapsed = useCallback(
    (collapsed: boolean) => {
      if (rightCollapsed != null) {
        onRightCollapsedChange?.(collapsed);

        return;
      }

      setRightCollapsedState(collapsed);
    },
    [rightCollapsed, onRightCollapsedChange],
  );

  const toggleLeftSidebar = useCallback(() => {
    updateLeftCollapsed(!isLeftCollapsed);
  }, [isLeftCollapsed, updateLeftCollapsed]);

  const toggleRightSidebar = useCallback(() => {
    updateRightCollapsed(!isRightCollapsed);
  }, [isRightCollapsed, updateRightCollapsed]);

  useScrollShadows({ scrollRef: mainScrollRef, viewportRef: mainScrollViewportRef });
  useScrollShadows({
    enabled: hasLeftSidebar && !isLeftCollapsed,
    scrollRef: leftSidebarScrollRef,
    viewportRef: leftSidebarScrollViewportRef,
  });
  useScrollShadows({
    enabled: hasRightSidebar && !isRightCollapsed,
    scrollRef: rightSidebarScrollRef,
    viewportRef: rightSidebarScrollViewportRef,
  });

  return (
    <InnerPanelRoot $isDrawerMode={isDrawerMode} style={styleOverrides}>
      <ThreePanelBody
        $hasLeftSidebar={hasLeftSidebar}
        $hasRightSidebar={hasRightSidebar}
        $isDrawerMode={isDrawerMode}
        $leftCollapsed={isLeftCollapsed}
        $rightCollapsed={isRightCollapsed}
      >
        {hasLeftSidebar && !isDrawerMode && (
          <LeftPanelSidebar $collapsed={isLeftCollapsed} aria-hidden={isLeftCollapsed} data-panel-sidebar="left">
            <LeftPanelSidebarInner>
              <LeftPanelSidebarScrollViewport ref={leftSidebarScrollViewportRef}>
                <LeftPanelSidebarScrollArea $padded={leftSidebarPadded} ref={leftSidebarScrollRef}>
                  {leftSidebar}
                </LeftPanelSidebarScrollArea>
              </LeftPanelSidebarScrollViewport>
            </LeftPanelSidebarInner>
          </LeftPanelSidebar>
        )}
        {hasLeftSidebar && isDrawerMode && (
          <DrawerPanel
            accessibilityTitle="Left sidebar"
            isOpen={!isLeftCollapsed}
            onClose={() => updateLeftCollapsed(true)}
            side="left"
            widthOverride={leftSidebarWidth}
          >
            <LeftPanelSidebarInner>
              <LeftPanelSidebarScrollViewport ref={leftSidebarScrollViewportRef}>
                <LeftPanelSidebarScrollArea $padded={leftSidebarPadded} ref={leftSidebarScrollRef}>
                  {leftSidebar}
                </LeftPanelSidebarScrollArea>
              </LeftPanelSidebarScrollViewport>
            </LeftPanelSidebarInner>
          </DrawerPanel>
        )}
        <MainPanel>
          {showSidebarControls && (hasLeftSidebar || hasRightSidebar) && (
            <FloatingSidebarControls>
              {hasLeftSidebar && (
                <SidebarControlButton
                  $active={!isLeftCollapsed}
                  $visible={true}
                  aria-label={isLeftCollapsed ? 'Expand left sidebar' : 'Collapse left sidebar'}
                  hoverChildren={<LayoutSidebarLeftDock />}
                  onClick={toggleLeftSidebar}
                  type="button"
                >
                  {isLeftCollapsed ? <LayoutSidebarLeftOff /> : <LayoutSidebarLeft />}
                </SidebarControlButton>
              )}
              {hasRightSidebar && (
                <SidebarControlButton
                  $active={!isRightCollapsed}
                  $visible={true}
                  aria-label={isRightCollapsed ? 'Expand right sidebar' : 'Collapse right sidebar'}
                  hoverChildren={<LayoutSidebarRightDock />}
                  onClick={toggleRightSidebar}
                  type="button"
                >
                  {isRightCollapsed ? <LayoutSidebarRightOff /> : <LayoutSidebarRight />}
                </SidebarControlButton>
              )}
            </FloatingSidebarControls>
          )}
          <MainScrollViewport ref={mainScrollViewportRef}>
            <MainScrollArea ref={mainScrollRef}>
              <PanelContentInner $padded={mainContentPadded}>{children}</PanelContentInner>
            </MainScrollArea>
          </MainScrollViewport>
        </MainPanel>
        {hasRightSidebar && !isDrawerMode && (
          <RightPanelSidebar $collapsed={isRightCollapsed} aria-hidden={isRightCollapsed} data-panel-sidebar="right">
            <RightPanelSidebarInner>
              <RightPanelSidebarScrollViewport ref={rightSidebarScrollViewportRef}>
                <RightPanelSidebarScrollArea $padded={rightSidebarPadded} ref={rightSidebarScrollRef}>
                  {rightSidebar}
                </RightPanelSidebarScrollArea>
              </RightPanelSidebarScrollViewport>
            </RightPanelSidebarInner>
          </RightPanelSidebar>
        )}
        {hasRightSidebar && isDrawerMode && (
          <DrawerPanel
            accessibilityTitle="Right sidebar"
            isOpen={!isRightCollapsed}
            onClose={() => updateRightCollapsed(true)}
            side="right"
            widthOverride={rightSidebarWidth}
          >
            <RightPanelSidebarInner>
              <RightPanelSidebarScrollViewport ref={rightSidebarScrollViewportRef}>
                <RightPanelSidebarScrollArea $padded={rightSidebarPadded} ref={rightSidebarScrollRef}>
                  {rightSidebar}
                </RightPanelSidebarScrollArea>
              </RightPanelSidebarScrollViewport>
            </RightPanelSidebarInner>
          </DrawerPanel>
        )}
      </ThreePanelBody>
    </InnerPanelRoot>
  );
};
